import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertCircle,
    ArrowRight,
    CheckCircle,
    Cloud,
    Copy,
    Download,
    FileCode,
    Filter,
    Loader2,
    Play,
    Plus,
    Save,
    Smartphone,
    Trash2,
    Wand2,
    XCircle,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { tokenize, getTestCaseText } from '../../utils/locatorMatch';
import { MobileInspectorModal } from './MobileInspectorModal';
import { loadJobResult, submitJobOperation, waitForJobResult } from '../../services/jobs';

// Opened in a new tab when a BrowserStack run starts, so the user can watch the cloud
// device live. The exact per-session link is surfaced in Run Output after the run.
const BROWSERSTACK_DASHBOARD_URL = 'https://app-automate.browserstack.com/dashboard/v2';

// Appium Android locator strategies (mirrors MobilePOMStep).
const STRATEGY_OPTIONS = [
    { value: 'accessibility-id', label: 'accessibility-id' },
    { value: 'id', label: 'id (resource-id)' },
    { value: 'xpath', label: 'xpath' },
    { value: 'android-uiautomator', label: 'android-uiautomator' },
    { value: 'class-name', label: 'class-name' },
    { value: 'text', label: 'text' },
];

const TABS = [
    { id: 'script', label: 'Script' },
    { id: 'poms', label: 'POMs' },
    { id: 'run', label: 'Run Output' },
    { id: 'metadata', label: 'Metadata' },
];
const MOBILE_SCRIPT_AUTOSAVE_DELAY_MS = 900;

const getCaseKey = (testCase, idx) =>
    testCase._case_key || `${testCase.test_case_no || 'case'}::${idx}`;

const getCaseId = (testCase = {}) =>
    String(testCase?.test_case_no || testCase?.testcase_no || testCase?.id || '').trim();

// Mirror the web Scripts card: the real per-case description lives on
// `test_description` / `test_name` (falling back to other title fields), NOT
// the generic "Test Case <id>" placeholder.
const getCaseTitle = (testCase) =>
    testCase.test_description
    || testCase.test_name
    || testCase.scenario
    || testCase.title
    || testCase.test_case_title
    || testCase.description
    || `Test Case ${testCase.test_case_no || ''}`.trim();

const extractScript = (payload) =>
    payload?.script || payload?.typescriptCode || payload?.code || payload?.spec || '';

const newLocalId = () => Math.random().toString(36).slice(2);

const asLines = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (typeof value === 'string') return value.split('\n').map((l) => l.trim()).filter(Boolean);
    return [];
};

const tabFlowsText = (tc) => {
    const v = tc.tab_flows ?? tc.tabFlows;
    if (Array.isArray(v)) return v.filter(Boolean).join(' -> ');
    return v ? String(v) : '';
};

// Canonical form fields, detected from a screen's locators, that the generated
// script will type into — so the input set carries matching {USER_INPUT:<field>}
// entries and the run fills them from data/mobile_inputs.json. The Login screen's
// email/password ARE the login credentials, so they map to the dedicated
// loginEmail/loginPassword tokens (kept separate from any form's own email field).
const FIELD_PATTERNS = [
    ['fullName', /full[\s_-]?name|fullname/],
    ['mobile', /mobile|phone/],
    ['loanType', /loan[\s_-]?type/],
    ['city', /\bcity\b/],
    ['state', /\bstate\b/],
    ['source', /\bsource\b/],
    ['status', /\bstatus\b/],
    ['email', /e[-\s]?mail|mail/],
    ['password', /pass(word|wd)?|pwd/],
];
const screenInputFields = (screen) => {
    const text = (loc) => `${loc?.name || ''} ${loc?.value || ''} ${loc?.description || ''}`.toLowerCase();
    const locs = screen?.locators || [];
    const found = [];
    FIELD_PATTERNS.forEach(([field, re]) => {
        if (locs.some((l) => re.test(text(l)))) found.push(field);
    });
    const name = (screen?.screen || '').toLowerCase();
    if (name === 'login') {
        // On Login, the email/password fields ARE the login credentials.
        return found.map((f) => (f === 'email' ? 'loginEmail' : f === 'password' ? 'loginPassword' : f));
    }
    if (name === 'register') {
        // Register is a pre-auth screen — no prior login needed.
        return found;
    }
    // Every other screen is behind login: the generated script logs in first, so
    // seed the login credentials in addition to this screen's own form fields.
    return ['loginEmail', 'loginPassword', ...found];
};

// Sensible starting values so the metadata ALWAYS shows every field with a value
// (never empty / never a bare placeholder). When you click Generate Script the AI
// regenerates these per the test case (applyGeneratedInputs). The run fills the app
// strictly from this metadata — these field names match the script's placeholders.
const DEFAULT_INPUT_VALUES = {
    loginEmail: 'testuser@example.com',
    loginPassword: 'Test@1234',
    fullName: 'Asha Rao',
    mobile: '9876543210',
    email: 'asha.rao@example.com',
    password: 'Test@1234',
    loanType: 'Home Loan',
    city: 'Bangalore',
    state: 'Karnataka',
    source: 'Website',
    status: 'New',
};

// A sensible value for any field name, so no placeholder is ever left without a value.
const defaultForField = (field) => {
    const f = String(field).trim().toLowerCase();
    if (Object.prototype.hasOwnProperty.call(DEFAULT_INPUT_VALUES, field)) return DEFAULT_INPUT_VALUES[field];
    if (f.includes('loginemail')) return 'testuser@example.com';
    if (f.includes('loginpassword') || f.includes('password') || f.includes('pwd')) return 'Test@1234';
    if (f.includes('mobile') || f.includes('phone')) return '9876543210';
    if (f.includes('email') || f.includes('mail')) return 'asha.rao@example.com';
    if (f.includes('fullname') || f === 'name' || f.includes('full_name')) return 'Asha Rao';
    if (f.includes('loan')) return 'Home Loan';
    if (f.includes('city')) return 'Bangalore';
    if (f.includes('state')) return 'Karnataka';
    if (f.includes('source')) return 'Website';
    if (f.includes('status')) return 'New';
    return 'Test Value';
};

// Build the complete set of metadata values for a generated script: start from the AI's
// values, then ensure EVERY {USER_INPUT:<field>} placeholder in the script has a value
// (filling any the model missed) so nothing is left unfilled at run time.
const completeInputsForScript = (script, generated) => {
    const out = { ...(generated && typeof generated === 'object' ? generated : {}) };
    const placeholders = [...String(script || '').matchAll(/\{USER_INPUT:([^}]+)\}/g)].map((m) => m[1].trim());
    placeholders.forEach((ph) => {
        if (!Object.prototype.hasOwnProperty.call(out, ph) || !String(out[ph] ?? '').trim()) {
            out[ph] = defaultForField(ph);
        }
    });
    return out;
};

// Replace the metadata with exactly the AI-generated fields/values from script
// generation, so the metadata's field names match the script's {USER_INPUT:<field>}
// placeholders one-to-one and every value that gets typed in the app is shown here.
const applyGeneratedInputs = (inputSets, generated) => {
    const entries = Object.entries(generated || {}).filter(([f]) => String(f).trim());
    if (!entries.length) return inputSets;
    const rows = entries.map(([field, value]) => ({
        id: newLocalId(),
        field: String(field).trim(),
        value: value == null ? '' : String(value),
    }));
    const primary = (inputSets && inputSets[0]) || {};
    return [{ id: primary.id || newLocalId(), name: primary.name || 'Test Data', rows }];
};

// Make sure the (primary) input set carries the screen's form fields WITH values so
// the test case's metadata contains every field that gets filled in the LOS app, and
// the run takes its inputs straight from this metadata. Existing rows are kept as-is
// (user edits win); missing fields are added pre-filled with their default value.
const withSeededInputs = (inputSets, fields) => {
    if (!fields.length) return inputSets;
    const sets = (inputSets && inputSets.length)
        ? inputSets
        : [{ id: newLocalId(), name: 'Set 1', rows: [] }];
    return sets.map((set, i) => {
        if (i !== 0) return set;
        const rows = (set.rows || []).map((r) => ({ ...r }));
        fields.forEach((field) => {
            const exists = rows.some((r) => String(r.field || '').trim().toLowerCase() === field.toLowerCase());
            if (!exists) rows.push({ id: newLocalId(), field, value: DEFAULT_INPUT_VALUES[field] || '' });
        });
        return { ...set, rows };
    });
};

// Convert the editable input sets back into the API shape for generation.
const serializeInputSets = (inputSets) =>
    (inputSets || [])
        .map((s) => ({
            set_name: s.name,
            user_inputs: (s.rows || [])
                .filter((r) => r.field.trim())
                .map((r) => ({ field: r.field.trim(), value: r.value })),
        }))
        .filter((s) => s.user_inputs.length);

const deserializeInputSets = (inputSets) =>
    (Array.isArray(inputSets) ? inputSets : [])
        .map((setItem, setIndex) => {
            const rows = Array.isArray(setItem?.rows)
                ? setItem.rows
                : (Array.isArray(setItem?.user_inputs) ? setItem.user_inputs : []);
            return {
                id: setItem?.id || newLocalId(),
                name: String(setItem?.name || setItem?.set_name || `Set ${setIndex + 1}`).trim() || `Set ${setIndex + 1}`,
                rows: rows
                    .filter((row) => row && typeof row === 'object')
                    .map((row) => ({
                        id: row.id || newLocalId(),
                        field: String(row.field || row.name || row.label || '').trim(),
                        value: row.value == null ? '' : String(row.value),
                    }))
                    .filter((row) => row.field),
            };
        })
        .filter((setItem) => setItem.rows.length);

/**
 * Mobile automation builder that mirrors the web Scripts page: a left list of
 * test cases (pick one to inspect) and a right detail panel with Script / POMs /
 * Run Output / Metadata tabs. Locators are auto-filled from the imported library.
 */
export const MobileAutoTestStep = ({
    pdfId,
    editedTestCases,
    processingResult,
    onClose,
    onContinue,
    testingType = 'mobile',
    showBackButton = true,
}) => {
    const sectionNumber = Number(processingResult?.section_number || 0) || null;

    const normalizedTestCases = useMemo(() => {
        if (!editedTestCases) return [];
        const tag = (tc, idx, category) => ({
            ...tc,
            _case_key: `${category}::${tc.test_case_no || 'case'}::${idx}`,
            _category: tc.category || category,
        });
        if (Array.isArray(editedTestCases)) {
            return editedTestCases.map((tc, idx) => tag(tc, idx, tc.category || 'all'));
        }
        if (typeof editedTestCases === 'object') {
            const cases = [];
            ['positive', 'negative'].forEach((category) => {
                if (Array.isArray(editedTestCases[category])) {
                    editedTestCases[category].forEach((tc, idx) => cases.push(tag(tc, idx, category)));
                }
            });
            return cases;
        }
        return [];
    }, [editedTestCases]);

    const [caseState, setCaseState] = useState({});
    const [selectedKey, setSelectedKey] = useState(null);
    // Live-device locator inspector ("Pick from device"), same as the POM step.
    const [inspectorOpen, setInspectorOpen] = useState(false);
    // Section-wide "Download report (Excel)" state (not tied to a single case).
    const [excelReport, setExcelReport] = useState({ loading: false, error: null });
    const [activeTab, setActiveTab] = useState('poms');
    const [filter, setFilter] = useState('all');
    const [automationRunning, setAutomationRunning] = useState(false);
    // Where runs execute: 'local' emulator (Android Studio) or 'browserstack'
    // cloud device. The user picks this at the automation step; the backend reads
    // it as the run `target`. Defaults to the local emulator.
    const [runTarget, setRunTarget] = useState(testingType === 'browserstack' ? 'browserstack' : 'local');
    // Multi-select: keys of test cases checked for batch generate/run/report.
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    // Progress label shown while a batch action (generate/run/report) runs.
    const [batchAction, setBatchAction] = useState(null); // { kind, done, total } | null

    // The POMs the user built (with "Pick from device") + their real locators.
    const [builtPoms, setBuiltPoms] = useState([]);
    const [pomsLoading, setPomsLoading] = useState(true);
    const [savedScriptsByCaseId, setSavedScriptsByCaseId] = useState({});
    const [dirtyScriptKeys, setDirtyScriptKeys] = useState(() => new Set());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!pdfId) {
                setSavedScriptsByCaseId({});
                return;
            }
            try {
                const params = new URLSearchParams({ pdf_id: pdfId });
                if (sectionNumber) params.set('section_number', String(sectionNumber));
                const res = await apiFetch(`${API_ENDPOINTS.APPIUM_SCRIPTS}?${params.toString()}`, { method: 'GET' });
                if (!res.ok) {
                    setSavedScriptsByCaseId({});
                    return;
                }
                const payload = await res.json().catch(() => ({}));
                if (!cancelled) {
                    setSavedScriptsByCaseId(
                        payload?.scripts && typeof payload.scripts === 'object'
                            ? payload.scripts
                            : {}
                    );
                }
            } catch {
                if (!cancelled) setSavedScriptsByCaseId({});
            }
        })();
        return () => { cancelled = true; };
    }, [pdfId, sectionNumber]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!pdfId) { setPomsLoading(false); return; }
            setPomsLoading(true);
            try {
                const listRes = await apiFetch(`${API_ENDPOINTS.MOBILE_POMS}?pdf_id=${encodeURIComponent(pdfId)}`, { method: 'GET' });
                const pages = listRes.ok ? ((await listRes.json()).pages || []) : [];
                const full = await Promise.all(pages.map(async (p) => {
                    try {
                        const r = await apiFetch(`${API_ENDPOINTS.MOBILE_POMS}/${encodeURIComponent(p.pageName)}?pdf_id=${encodeURIComponent(pdfId)}`, { method: 'GET' });
                        if (!r.ok) return null;
                        const cfg = await r.json();
                        return {
                            pageName: cfg.pageName || p.pageName,
                            appPackage: cfg.appPackage || p.appPackage || 'com.sample.los',
                            appActivity: cfg.appActivity || '.MainActivity',
                            locators: (cfg.locators || []).map((l) => ({ name: l.name, strategy: l.strategy, value: l.value, description: l.description || '' })),
                        };
                    } catch { return null; }
                }));
                if (!cancelled) setBuiltPoms(full.filter(Boolean));
            } catch {
                if (!cancelled) setBuiltPoms([]);
            } finally {
                if (!cancelled) setPomsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [pdfId]);

    // Pick the built POM whose name best matches a test case (token overlap).
    const pickPomForCase = (tc) => {
        if (!builtPoms.length) return null;
        const caseTokens = new Set(tokenize(getTestCaseText(tc)));
        let best = builtPoms[0];
        let bestScore = -1;
        builtPoms.forEach((pom) => {
            let score = 0;
            tokenize(pom.pageName).forEach((t) => { if (caseTokens.has(t)) score += 1; });
            if (score > bestScore) { bestScore = score; best = pom; }
        });
        return best;
    };

    // Seed each case from the user's built POMs (best name match). No static catalog.
    useEffect(() => {
        if (!builtPoms.length) return;
        setCaseState((prev) => {
            const next = { ...prev };
            normalizedTestCases.forEach((tc, idx) => {
                const key = getCaseKey(tc, idx);
                const current = next[key];
                const pom = pickPomForCase(tc);
                const pomLocators = (pom?.locators || []).map((loc) => ({ ...loc, _id: newLocalId() }));
                const seededInputs = withSeededInputs(
                    current?.inputSets || [],
                    screenInputFields({ screen: pom?.pageName || '', locators: pom?.locators || [] })
                );
                if (current) {
                    if (current.pomName && (current.locators || []).length) return;
                    next[key] = {
                        ...current,
                        screenName: current.screenName || pom?.pageName || '',
                        confident: current.confident ?? true,
                        pomName: current.pomName || pom?.pageName || '',
                        appPackage: current.appPackage || pom?.appPackage || 'com.sample.los',
                        appActivity: current.appActivity || pom?.appActivity || '.MainActivity',
                        locators: (current.locators || []).length ? current.locators : pomLocators,
                        inputSets: seededInputs.length ? seededInputs : current.inputSets,
                    };
                    return;
                }
                next[key] = {
                    screenName: pom?.pageName || '',
                    confident: true,
                    pomName: pom?.pageName || '',
                    appPackage: pom?.appPackage || 'com.sample.los',
                    appActivity: pom?.appActivity || '.MainActivity',
                    locators: pomLocators,
                    inputSets: seededInputs,
                    script: '',
                    status: 'idle',
                    error: null,
                    run: null,
                    assumptions: [],
                    warnings: [],
                };
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedTestCases, builtPoms]);

    useEffect(() => {
        if (!savedScriptsByCaseId || !Object.keys(savedScriptsByCaseId).length) return;
        setCaseState((prev) => {
            const next = { ...prev };
            normalizedTestCases.forEach((tc, idx) => {
                const caseId = getCaseId(tc);
                const key = getCaseKey(tc, idx);
                const saved = savedScriptsByCaseId[key] || savedScriptsByCaseId[caseId];
                const script = String(saved?.script || '').trim();
                if (!script) return;

                const savedPomName = Array.isArray(saved?.selected_poms)
                    ? String(saved.selected_poms.find(Boolean) || '').trim()
                    : '';
                const pom = (savedPomName && builtPoms.find((p) => p.pageName === savedPomName))
                    || pickPomForCase(tc);
                const current = next[key] || {};
                if (String(current.script || '').trim()) return;

                const restoredInputSets = deserializeInputSets(saved.input_sets);
                next[key] = {
                    screenName: current.screenName || pom?.pageName || savedPomName || '',
                    confident: current.confident ?? true,
                    pomName: current.pomName || pom?.pageName || savedPomName || '',
                    appPackage: current.appPackage || pom?.appPackage || 'com.sample.los',
                    appActivity: current.appActivity || pom?.appActivity || '.MainActivity',
                    locators: (current.locators || []).length
                        ? current.locators
                        : (pom?.locators || []).map((loc) => ({ ...loc, _id: newLocalId() })),
                    inputSets: restoredInputSets.length ? restoredInputSets : (current.inputSets || []),
                    script,
                    status: 'saved',
                    error: null,
                    run: current.run || null,
                    assumptions: Array.isArray(saved.assumptions) ? saved.assumptions : (current.assumptions || []),
                    warnings: Array.isArray(saved.warnings) ? saved.warnings : (current.warnings || []),
                    model: saved.model || current.model || null,
                };
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedTestCases, savedScriptsByCaseId, builtPoms]);

    useEffect(() => {
        if (!selectedKey && normalizedTestCases.length) {
            setSelectedKey(getCaseKey(normalizedTestCases[0], 0));
        }
    }, [normalizedTestCases, selectedKey]);

    const filteredCases = useMemo(() => {
        if (filter === 'all') return normalizedTestCases;
        return normalizedTestCases.filter((tc) => (tc._category || '').toLowerCase() === filter);
    }, [normalizedTestCases, filter]);

    const counts = useMemo(() => ({
        all: normalizedTestCases.length,
        positive: normalizedTestCases.filter((tc) => (tc._category || '').toLowerCase() === 'positive').length,
        negative: normalizedTestCases.filter((tc) => (tc._category || '').toLowerCase() === 'negative').length,
    }), [normalizedTestCases]);

    const indexByKey = useMemo(() => {
        const map = {};
        normalizedTestCases.forEach((tc, idx) => { map[getCaseKey(tc, idx)] = idx; });
        return map;
    }, [normalizedTestCases]);

    const selectedCase = selectedKey != null
        ? normalizedTestCases[indexByKey[selectedKey]]
        : null;
    const selectedState = selectedKey != null ? caseState[selectedKey] : null;

    const patchCase = (key, patch) =>
        setCaseState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const markScriptDirty = (key) => {
        if (!key) return;
        setDirtyScriptKeys((prev) => new Set([...prev, key]));
    };

    const handleScreenChange = (key, pomName) => {
        const pom = builtPoms.find((p) => p.pageName === pomName);
        if (!pom) return;
        patchCase(key, {
            screenName: pomName,
            pomName: pom.pageName,
            appPackage: pom.appPackage || 'com.sample.los',
            appActivity: pom.appActivity || '.MainActivity',
            locators: (pom.locators || []).map((loc) => ({ ...loc, _id: newLocalId() })),
            // Seed the chosen POM's form fields so they get filled at run time.
            inputSets: withSeededInputs(caseState[key]?.inputSets, screenInputFields({ screen: pom.pageName, locators: pom.locators || [] })),
            status: 'idle',
            script: '',
            run: null,
            error: null,
        });
    };

    const updateLocator = (key, id, field, value) =>
        patchCase(key, {
            locators: caseState[key].locators.map((loc) =>
                loc._id === id ? { ...loc, [field]: value } : loc
            ),
        });

    const addLocator = (key) =>
        patchCase(key, {
            locators: [
                ...caseState[key].locators,
                { _id: newLocalId(), name: '', strategy: 'accessibility-id', value: '', description: '' },
            ],
        });

    const removeLocator = (key, id) =>
        patchCase(key, { locators: caseState[key].locators.filter((loc) => loc._id !== id) });

    // Append a locator captured from the live device via the inspector.
    const addLocatorFromInspector = (key, picked) => {
        if (!key || !caseState[key]) return;
        const strategy = STRATEGY_OPTIONS.some((o) => o.value === picked?.strategy)
            ? picked.strategy
            : 'accessibility-id';
        patchCase(key, {
            locators: [
                ...caseState[key].locators,
                { _id: newLocalId(), name: picked?.name || '', strategy, value: picked?.value || '', description: '' },
            ],
        });
    };


    const copyScript = (script) => {
        if (script && navigator.clipboard) navigator.clipboard.writeText(script).catch(() => {});
    };

    const downloadScript = (testCase, script) => {
        if (!script) return;
        const safe = String(testCase?.test_case_no || 'spec').replace(/[^A-Za-z0-9_.-]/g, '_');
        const blob = new Blob([script], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safe}.spec.ts`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // --- User Input Sets editing (the test data the script uses) ---
    const addInputSet = (key) => {
        const sets = caseState[key].inputSets || [];
        if (sets.length >= 3) return; // up to 3, like the web flow
        // New sets copy field names from the first set, values blank.
        const template = (sets[0]?.rows || []).map((r) => ({ id: newLocalId(), field: r.field, value: '' }));
        patchCase(key, {
            inputSets: [...sets, { id: newLocalId(), name: `Set ${sets.length + 1}`, rows: template.length ? template : [{ id: newLocalId(), field: '', value: '' }] }],
        });
        markScriptDirty(key);
    };
    const removeInputSet = (key, setId) => {
        patchCase(key, { inputSets: (caseState[key].inputSets || []).filter((s) => s.id !== setId) });
        markScriptDirty(key);
    };
    const addInputRow = (key, setId) => {
        patchCase(key, {
            inputSets: (caseState[key].inputSets || []).map((s) =>
                s.id === setId ? { ...s, rows: [...s.rows, { id: newLocalId(), field: '', value: '' }] } : s
            ),
        });
        markScriptDirty(key);
    };
    const updateInputRow = (key, setId, rowId, field, value) => {
        patchCase(key, {
            inputSets: (caseState[key].inputSets || []).map((s) =>
                s.id === setId
                    ? { ...s, rows: s.rows.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)) }
                    : s
            ),
        });
        markScriptDirty(key);
    };
    const removeInputRow = (key, setId, rowId) => {
        patchCase(key, {
            inputSets: (caseState[key].inputSets || []).map((s) =>
                s.id === setId ? { ...s, rows: s.rows.filter((r) => r.id !== rowId) } : s
            ),
        });
        markScriptDirty(key);
    };

    const ensurePomSaved = async (state) => {
        const locators = state.locators
            .filter((loc) => loc.name.trim() && loc.value.trim())
            .map((loc) => ({
                name: loc.name.trim(),
                strategy: loc.strategy,
                value: loc.value.trim(),
                description: loc.description || '',
            }));
        if (!locators.length) throw new Error('Add at least one locator with a name and value.');

        const createRes = await apiFetch(API_ENDPOINTS.MOBILE_POM_CREATE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pdf_id: pdfId,
                pageName: state.pomName,
                appPackage: state.appPackage.trim(),
                appActivity: state.appActivity.trim(),
                locators,
                functions: [],
            }),
        });
        if (createRes.ok) return;
        if (createRes.status === 409) {
            const updateRes = await apiFetch(`${API_ENDPOINTS.MOBILE_POM_UPDATE}/${state.pomName}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    appPackage: state.appPackage.trim(),
                    appActivity: state.appActivity.trim(),
                    locatorsToAdd: locators,
                    locatorsToDelete: [],
                    functionsToAdd: [],
                    functionsToDelete: [],
                }),
            });
            if (!updateRes.ok) {
                const err = await updateRes.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update mobile POM.');
            }
            return;
        }
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save mobile POM.');
    };

    const persistScriptForKey = async (key, overrides = {}) => {
        const testCase = normalizedTestCases[indexByKey[key]];
        const state = { ...(caseState[key] || {}), ...overrides };
        const script = String(state.script || '').trim();
        if (!script || !testCase) return false;
        patchCase(key, { status: 'saving-script', error: null });
        try {
            const inputSetsPayload = serializeInputSets(state.inputSets || []);
            const res = await apiFetch(API_ENDPOINTS.APPIUM_SAVE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    section_number: sectionNumber,
                    test_case_id: key || getCaseId(testCase),
                    test_case: { ...testCase, user_input_sets: inputSetsPayload },
                    script,
                    input_sets: inputSetsPayload,
                    assumptions: state.assumptions || [],
                    warnings: state.warnings || [],
                    model: state.model || null,
                    selected_poms: state.pomName ? [state.pomName] : [],
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save script.');
            }
            patchCase(key, { status: 'saved' });
            setDirtyScriptKeys((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
            return true;
        } catch (err) {
            patchCase(key, { error: err.message || 'Save failed.', status: 'error' });
            return false;
        }
    };

    // Generate the Appium script for one specific test case. `switchTab` is off
    // during batch generation so the UI doesn't flip tabs for every case; when the
    // caller already saved the POM (batch dedupes shared screens), `savePom` is off.
    const generateForKey = async (key, { switchTab = true, savePom = true } = {}) => {
        const testCase = normalizedTestCases[indexByKey[key]];
        const state = caseState[key];
        if (!state || !testCase) return false;
        if (!String(state.pomName || '').trim()) {
            patchCase(key, { error: 'POM name is required.', status: 'error' });
            return false;
        }
        patchCase(key, { status: savePom ? 'saving' : 'generating', error: null });
        try {
            if (savePom) await ensurePomSaved(state);
            patchCase(key, { status: 'generating' });
            // Send the test case carrying the (possibly edited) input sets so the
            // backend builds {USER_INPUT:<field>} placeholders from this data only.
            const testCaseForGen = { ...testCase, user_input_sets: serializeInputSets(state.inputSets) };
            const res = await apiFetch(API_ENDPOINTS.APPIUM_GENERATE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    section_number: sectionNumber,
                    test_case: testCaseForGen,
                    selected_poms: [state.pomName],
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to generate Appium script.');
            }
            const result = await res.json();
            const script = extractScript(result);
            // Build the metadata so it has a value for EVERY {USER_INPUT:field} the script
            // fills — the AI's generated values plus a sensible default for anything missed.
            const completeInputs = completeInputsForScript(script, result.user_inputs);
            const nextPatch = {
                script,
                assumptions: Array.isArray(result.assumptions) ? result.assumptions : [],
                warnings: Array.isArray(result.warnings) ? result.warnings : [],
                inputSets: applyGeneratedInputs(state.inputSets, completeInputs),
                model: result.model || result.model_used || state.model || null,
                status: 'generated',
            };
            patchCase(key, nextPatch);
            const saved = await persistScriptForKey(key, nextPatch);
            if (switchTab) setActiveTab('metadata');
            return saved;
        } catch (err) {
            patchCase(key, { error: err.message || 'Generation failed.', status: 'error' });
            return false;
        }
    };

    const generateForCase = () => generateForKey(selectedKey, { switchTab: true });

    const saveScript = async () => { await persistScriptForKey(selectedKey); };

    useEffect(() => {
        if (!dirtyScriptKeys.size) return undefined;

        const timeoutId = window.setTimeout(() => {
            Array.from(dirtyScriptKeys).forEach((key) => {
                const state = caseState[key];
                if (!String(state?.script || '').trim()) return;
                if (['saving', 'generating', 'saving-script', 'running'].includes(state.status)) return;
                persistScriptForKey(key).catch(() => {});
            });
        }, MOBILE_SCRIPT_AUTOSAVE_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
        // persistScriptForKey uses the latest case snapshot; dirty keys are the save trigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseState, dirtyScriptKeys]);

    // Run a single case's generated script on the emulator. Boots the device +
    // Appium server (backend) and drives the spec, then records the result on the
    // case. Returns the run result (or null when there's nothing to run). Shared
    // by the per-case "Run on emulator" button and by "Continue to Automation".
    const runCase = async (key, { switchTab = true } = {}) => {
        const testCase = normalizedTestCases[indexByKey[key]];
        const state = caseState[key];
        if (!state?.script || !testCase) return null;
        // Resolve the chosen input set's real values so the backend fills the
        // script's {USER_INPUT:field} placeholders before running on the device.
        const sets = state.inputSets || [];
        const chosenSet = sets.find((s) => s.id === state.runSetId) || sets[0] || null;
        const userInputs = (chosenSet?.rows || [])
            .filter((r) => String(r.field || '').trim())
            .map((r) => ({ field: String(r.field).trim(), value: r.value ?? '' }));
        patchCase(key, { status: 'running', error: null });
        if (switchTab) setActiveTab('run');
        // Auto-open the BrowserStack dashboard so the live cloud device is visible while
        // the run executes. Only for a single explicit run (batch opens once, below).
        if (runTarget === 'browserstack' && switchTab && typeof window !== 'undefined') {
            window.open(BROWSERSTACK_DASHBOARD_URL, '_blank', 'noopener,noreferrer');
        }
        try {
            const { job } = await submitJobOperation(API_ENDPOINTS.APPIUM_RUN_QUEUE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    section_number: sectionNumber,
                    test_case_id: testCase.test_case_no,
                    script: state.script,
                    app_package: state.appPackage,
                    app_activity: state.appActivity,
                    user_inputs: userInputs,
                    set_name: chosenSet?.name || null,
                    target: runTarget,
                }),
            }, { errorMessage: 'Failed to queue Appium script.' });
            const snapshot = await waitForJobResult(job);
            const resultPayload = await loadJobResult(snapshot.job);
            const result = Array.isArray(resultPayload?.items) ? resultPayload.items[0] : resultPayload;
            patchCase(key, { run: result, status: result.passed ? 'run-passed' : 'run-failed' });
            return result;
        } catch (err) {
            const failRun = { passed: false, stderr: err.message || 'Run failed' };
            patchCase(key, { run: failRun, status: 'run-failed' });
            return failRun;
        }
    };

    const runScript = () => runCase(selectedKey);

    // Download a PDF report for the completed run. For a BrowserStack run the
    // backend pulls the cloud session's details, appium/device logs and video
    // link straight from BrowserStack, so the report is self-contained.
    const downloadReport = async () => {
        const key = selectedKey;
        const testCase = selectedCase;
        const state = caseState[key];
        const run = state?.run;
        if (!run || !testCase) return;
        patchCase(key, { reportLoading: true, error: null });
        try {
            const { job } = await submitJobOperation(API_ENDPOINTS.APPIUM_REPORT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pdf_id: pdfId,
                    run_id: run.run_id || null,
                    test_case_id: testCase.test_case_no,
                    test_case_title: testCase.test_case_title || testCase.title || '',
                    target: run.target || runTarget || 'local',
                    passed: !!run.passed,
                    exit_code: typeof run.exit_code === 'number' ? run.exit_code : null,
                    duration_ms: run.duration_ms ?? null,
                    set_name: run.set_name || null,
                    screenshots: typeof run.screenshots === 'number' ? run.screenshots : null,
                    runtime_notes: Array.isArray(run.runtime_notes) ? run.runtime_notes : [],
                    unresolved_inputs: Array.isArray(run.unresolved_inputs) ? run.unresolved_inputs : [],
                    stdout: run.stdout || '',
                    stderr: run.stderr || '',
                    script: state.script || '',
                    browserstack_session_url: run.browserstack_session_url || null,
                }),
            }, { errorMessage: 'Unable to queue PDF report.' });
            await waitForJobResult(job);
            window.open(`${API_BASE_URL}${API_ENDPOINTS.JOB_DOWNLOAD(job.id)}`, '_blank', 'noopener,noreferrer');
            patchCase(key, { reportLoading: false });
        } catch (err) {
            patchCase(key, { reportLoading: false, error: err.message || 'Report download failed.' });
        }
    };

    // Download the whole section's test cases as an Excel execution report,
    // annotated with each case's latest run status (Passed / Failed / Not Run)
    // and a metadata header (Date / Time / Author / BRD Name / Session).
    const downloadExcelReport = async () => {
        setExcelReport({ loading: true, error: null });
        try {
            const positive = [];
            const negative = [];
            normalizedTestCases.forEach((tc, idx) => {
                const run = caseState[getCaseKey(tc, idx)]?.run;
                const executionStatus = run
                    ? (run.exit_code === 0 ? 'Passed' : 'Failed')
                    : 'Not Run';
                const { _case_key, _category, ...rest } = tc;
                const row = { ...rest, execution_status: executionStatus };
                if (_category === 'negative') negative.push(row);
                else positive.push(row);
            });
            if (positive.length === 0 && negative.length === 0) {
                throw new Error('No test cases to export.');
            }
            const { job } = await submitJobOperation(API_ENDPOINTS.EXECUTION_REPORT_EXCEL(pdfId), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    section_title: processingResult?.section_title
                        || processingResult?.module
                        || 'Automation',
                    positive_test_cases: positive,
                    negative_test_cases: negative,
                    filename: 'testcases_execution_report.xlsx',
                    // Reflect the testing type the user SELECTED to generate the
                    // scripts (web / mobile / browserstack), keeping emulator-vs-cloud
                    // precision from the run target. Shown in the report's
                    // "Testing Type" header field.
                    testing_type: testingType === 'web'
                        ? 'web'
                        : (testingType === 'browserstack' || runTarget === 'browserstack')
                            ? 'browserstack'
                            : 'mobile-emulator',
                }),
            }, { errorMessage: 'Unable to queue Excel report.' });
            await waitForJobResult(job);
            window.open(`${API_BASE_URL}${API_ENDPOINTS.JOB_DOWNLOAD(job.id)}`, '_blank', 'noopener,noreferrer');
            setExcelReport({ loading: false, error: null });
        } catch (err) {
            setExcelReport({ loading: false, error: err.message || 'Excel report download failed.' });
        }
    };

    // "Continue to Automation": open the virtual device and run every test case
    // that has a generated script, one after another, so the emulator visibly
    // drives through the automation. When nothing is generated yet, fall back to
    // the parent's navigation so the button still moves the flow forward.
    const continueToAutomation = async () => {
        const runnableKeys = normalizedTestCases
            .map((tc, idx) => getCaseKey(tc, idx))
            .filter((key) => String(caseState[key]?.script || '').trim());
        if (runnableKeys.length === 0) {
            if (onContinue) onContinue();
            else onClose?.();
            return;
        }
        setAutomationRunning(true);
        // Open the BrowserStack dashboard once so the batch of cloud runs is watchable.
        if (runTarget === 'browserstack' && typeof window !== 'undefined') {
            window.open(BROWSERSTACK_DASHBOARD_URL, '_blank', 'noopener,noreferrer');
        }
        try {
            for (const key of runnableKeys) {
                setSelectedKey(key);
                setActiveTab('run');
                // eslint-disable-next-line no-await-in-loop
                await runCase(key, { switchTab: false });
            }
            // Leave the emulator running so it stays stable for the next run.
        } finally {
            setAutomationRunning(false);
        }
    };

    // ---- Multi-select batch actions -------------------------------------
    const toggleSelected = (key) =>
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });

    // Selected keys in list order — stable ordering for run + report.
    const orderedSelectedKeys = normalizedTestCases
        .map((tc, idx) => getCaseKey(tc, idx))
        .filter((key) => selectedKeys.has(key));
    const filteredKeys = filteredCases.map((tc, idx) => getCaseKey(tc, idx));
    const allFilteredSelected = filteredKeys.length > 0 && filteredKeys.every((k) => selectedKeys.has(k));
    const selectedWithScript = orderedSelectedKeys.filter((k) => String(caseState[k]?.script || '').trim());
    const selectedWithRun = orderedSelectedKeys.filter((k) => caseState[k]?.run);

    const toggleSelectAllFiltered = () =>
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) filteredKeys.forEach((k) => next.delete(k));
            else filteredKeys.forEach((k) => next.add(k));
            return next;
        });

    // Generate scripts for every selected case AT ONCE (in parallel). Each
    // generation is an independent API call, so they fire simultaneously. Shared
    // POMs are saved once up front (sequentially) to avoid file write races.
    const generateSelected = async () => {
        const keys = orderedSelectedKeys;
        if (!keys.length) return;
        setBatchAction({ kind: 'generate', done: 0, total: keys.length });
        try {
            // 1) Save each unique POM once (cases on the same screen share one POM).
            const savedPoms = new Set();
            for (const key of keys) {
                const st = caseState[key];
                const pom = String(st?.pomName || '').trim();
                if (!pom || savedPoms.has(pom)) continue;
                savedPoms.add(pom);
                patchCase(key, { status: 'saving', error: null });
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await ensurePomSaved(st);
                } catch (err) {
                    patchCase(key, { error: err.message || 'Failed to save POM.', status: 'error' });
                }
            }
            // 2) Generate every selected case simultaneously.
            let done = 0;
            await Promise.all(keys.map((key) =>
                generateForKey(key, { switchTab: false, savePom: false })
                    .finally(() => {
                        done += 1;
                        setBatchAction({ kind: 'generate', done, total: keys.length });
                    })
            ));
        } finally {
            setBatchAction(null);
        }
    };

    // Run every selected case that already has a generated script, in sequence.
    const runSelected = async () => {
        const keys = selectedWithScript;
        if (!keys.length) return;
        setAutomationRunning(true);
        setBatchAction({ kind: 'run', done: 0, total: keys.length });
        if (runTarget === 'browserstack' && typeof window !== 'undefined') {
            window.open(BROWSERSTACK_DASHBOARD_URL, '_blank', 'noopener,noreferrer');
        }
        try {
            let done = 0;
            for (const key of keys) {
                setSelectedKey(key);
                setActiveTab('run');
                // eslint-disable-next-line no-await-in-loop
                await runCase(key, { switchTab: false });
                done += 1;
                setBatchAction({ kind: 'run', done, total: keys.length });
            }
        } finally {
            setBatchAction(null);
            setAutomationRunning(false);
        }
    };

    // Download ONE combined PDF report for all selected cases that have run.
    const downloadSelectedReport = async () => {
        const keys = selectedWithRun;
        if (!keys.length) return;
        setBatchAction({ kind: 'report', done: 0, total: keys.length });
        try {
            const items = keys.map((key) => {
                const tc = normalizedTestCases[indexByKey[key]];
                const st = caseState[key];
                const run = st.run;
                return {
                    run_id: run.run_id || null,
                    test_case_id: tc?.test_case_no,
                    test_case_title: tc?.test_case_title || tc?.title || '',
                    target: run.target || runTarget || 'local',
                    passed: !!run.passed,
                    exit_code: typeof run.exit_code === 'number' ? run.exit_code : null,
                    duration_ms: run.duration_ms ?? null,
                    set_name: run.set_name || null,
                    screenshots: typeof run.screenshots === 'number' ? run.screenshots : null,
                    runtime_notes: Array.isArray(run.runtime_notes) ? run.runtime_notes : [],
                    unresolved_inputs: Array.isArray(run.unresolved_inputs) ? run.unresolved_inputs : [],
                    stdout: run.stdout || '',
                    stderr: run.stderr || '',
                    script: st.script || '',
                    browserstack_session_url: run.browserstack_session_url || null,
                };
            });
            const { job } = await submitJobOperation(API_ENDPOINTS.APPIUM_REPORT_BATCH, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pdf_id: pdfId, items }),
            }, { errorMessage: 'Unable to queue combined PDF report.' });
            await waitForJobResult(job);
            window.open(`${API_BASE_URL}${API_ENDPOINTS.JOB_DOWNLOAD(job.id)}`, '_blank', 'noopener,noreferrer');
        } catch (err) {
            if (selectedKey) patchCase(selectedKey, { error: err.message || 'Report download failed.' });
        } finally {
            setBatchAction(null);
        }
    };

    // Segmented "Run on" control — shared by the header and the automation footer
    // so the target is visible/changeable both before and at run time.
    const renderRunTargetToggle = () => (
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
            <button
                type="button"
                onClick={() => setRunTarget('local')}
                disabled={automationRunning || !!batchAction}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${
                    runTarget === 'local' ? 'bg-emerald-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                }`}
            >
                <Smartphone className="h-3.5 w-3.5" /> Emulator (Android Studio)
            </button>
            <button
                type="button"
                onClick={() => setRunTarget('browserstack')}
                disabled={automationRunning || !!batchAction}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${
                    runTarget === 'browserstack' ? 'bg-orange-500 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                }`}
            >
                <Cloud className="h-3.5 w-3.5" /> BrowserStack
            </button>
        </div>
    );

    if (normalizedTestCases.length === 0) {
        return (
            <div className="soft-card-muted border-dashed border-slate-300 text-center py-10">
                <Smartphone className="mx-auto h-10 w-10 text-slate-400" />
                <h4 className="mt-3 text-base font-semibold text-slate-900">No test cases available</h4>
                <p className="mt-1 text-sm text-slate-600">
                    Generate and edit test cases first, then return here to build mobile scripts.
                </p>
                {showBackButton && onClose && (
                    <button type="button" onClick={onClose} className="secondary-btn mt-4">Back</button>
                )}
            </div>
        );
    }

    if (!pomsLoading && builtPoms.length === 0) {
        return (
            <div className="soft-card-muted border-dashed border-slate-300 text-center py-10">
                <Smartphone className="mx-auto h-10 w-10 text-slate-400" />
                <h4 className="mt-3 text-base font-semibold text-slate-900">No POMs built yet</h4>
                <p className="mt-1 text-sm text-slate-600">
                    Scripts now use the POMs you build. Go back to the POM step, create a page object per
                    screen, and add its locators with <span className="font-semibold">Pick from device</span>.
                </p>
                {showBackButton && onClose && (
                    <button type="button" onClick={onClose} className="secondary-btn mt-4">Back</button>
                )}
            </div>
        );
    }

    const isBusy = ['saving', 'generating', 'saving-script', 'running'].includes(selectedState?.status);
    const runnableCount = normalizedTestCases.filter((tc, idx) =>
        String(caseState[getCaseKey(tc, idx)]?.script || '').trim()
    ).length;

    return (
        <div className="space-y-4">
            {/* Header */}
            <section className="soft-card border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-green-50 to-white p-4 md:p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-3">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 shadow-sm">
                            <Smartphone className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">Mobile Automation</p>
                                {runTarget === 'browserstack' && (
                                    <span className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700">
                                        <Cloud className="h-3 w-3" /> BrowserStack
                                    </span>
                                )}
                            </div>
                            <h3 className="mt-1 text-lg font-bold text-slate-900">Generate Appium (Android) scripts</h3>
                            <p className="mt-1 text-sm text-slate-600">
                                Pick a test case to inspect. Each case uses the POMs you built
                                ({builtPoms.length} POM{builtPoms.length === 1 ? '' : 's'}). Generate, then run
                                {runTarget === 'browserstack' ? ' on a BrowserStack cloud device.' : ' on the emulator.'}
                            </p>
                        </div>
                    </div>
                    {showBackButton && onClose && (
                        <button type="button" onClick={onClose} className="secondary-btn h-9 px-4 py-2 text-sm">Back</button>
                    )}
                </div>

                {/* Script Framework + batch actions — mirrors the web Scripts toolbar */}
                <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                            <Wand2 className="h-3.5 w-3.5" /> Script Framework
                        </span>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm">
                                Appium (Android)
                            </span>
                            <span className="text-xs text-slate-500">Appium is currently enabled for mobile script generation.</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {selectedKeys.size > 0 && (
                            <span className="text-sm text-slate-600">Selected: {selectedKeys.size}</span>
                        )}
                        <button
                            type="button"
                            onClick={toggleSelectAllFiltered}
                            disabled={filteredKeys.length === 0}
                            className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {allFilteredSelected ? 'Clear Selection' : 'Select All'}
                        </button>
                        <button
                            type="button"
                            onClick={generateSelected}
                            disabled={selectedKeys.size === 0 || !!batchAction || automationRunning}
                            className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {batchAction?.kind === 'generate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                            Generate Selected
                        </button>
                        <button
                            type="button"
                            onClick={runSelected}
                            disabled={selectedWithScript.length === 0 || !!batchAction || automationRunning}
                            className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {batchAction?.kind === 'run' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            Run Selected
                        </button>
                        <button
                            type="button"
                            onClick={downloadSelectedReport}
                            disabled={selectedWithRun.length === 0 || !!batchAction}
                            className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-slate-900 px-6 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {batchAction?.kind === 'report' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                            Report
                        </button>
                    </div>
                </div>

                {/* Category filter */}
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
                    <Filter className="h-4 w-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Filter:</span>
                    {[
                        ['all', `All (${counts.all})`, 'bg-slate-800 text-white', 'bg-slate-100 text-slate-700 hover:bg-slate-200'],
                        ['positive', `Positive (${counts.positive})`, 'bg-green-600 text-white', 'bg-green-100 text-green-700 hover:bg-green-200'],
                        ['negative', `Negative (${counts.negative})`, 'bg-red-600 text-white', 'bg-red-100 text-red-700 hover:bg-red-200'],
                    ].map(([id, label, activeCls, idleCls]) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setFilter(id)}
                            className={`rounded-full px-3 py-1 text-sm font-medium transition ${filter === id ? activeCls : idleCls}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Batch progress */}
                {batchAction && (
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {batchAction.kind === 'generate' ? 'Generating scripts' : batchAction.kind === 'run' ? 'Running automation' : 'Building report'}
                        {' '}{batchAction.done}/{batchAction.total}…
                    </div>
                )}

                {/* Run target — where the generated scripts execute. */}
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/70 p-2.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Run on</span>
                    {renderRunTargetToggle()}
                    <span className="text-xs text-slate-500">
                        {runTarget === 'browserstack'
                            ? 'Runs execute on a BrowserStack cloud device.'
                            : 'Runs execute on your local Android emulator (Android Studio).'}
                    </span>
                </div>
            </section>

            {/* Master-detail */}
            <div className="grid gap-4 lg:grid-cols-[320px,1fr]">
                {/* LEFT: test case list */}
                <section className="soft-card p-0">
                    <div className="border-b border-slate-200 px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">Test Cases</p>
                        <p className="mt-1 text-xs text-slate-500">Pick one case to inspect.</p>
                    </div>
                    <div className="max-h-[560px] divide-y divide-slate-100 overflow-y-auto">
                        {filteredCases.map((tc) => {
                            const idx = indexByKey[getCaseKey(tc, 0)] ?? normalizedTestCases.indexOf(tc);
                            const key = getCaseKey(tc, idx);
                            const state = caseState[key];
                            const active = key === selectedKey;
                            const checked = selectedKeys.has(key);
                            return (
                                <div
                                    key={key}
                                    className={`flex items-start gap-2 px-3 py-3 transition ${active ? 'bg-emerald-50/80 ring-1 ring-inset ring-emerald-300' : 'hover:bg-slate-50'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleSelected(key)}
                                        className="mt-1 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                        aria-label={`Select ${tc.test_case_no || 'test case'}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setSelectedKey(key)}
                                        className="min-w-0 flex-1 text-left"
                                    >
                                        <div className="flex items-center gap-2">
                                            <p className="truncate text-sm font-semibold text-slate-900">{tc.test_case_no || 'Test case'}</p>
                                            {tc._category && (
                                                <span className={`rounded px-2 py-0.5 text-[11px] font-semibold border ${
                                                    (tc._category || '').toLowerCase() === 'negative'
                                                        ? 'bg-red-50 text-red-700 border-red-200'
                                                        : 'bg-green-50 text-green-700 border-green-200'
                                                }`}>
                                                    {(tc._category || '').toLowerCase() === 'negative' ? 'Negative Test Case' : 'Positive Test Case'}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-1 line-clamp-2 text-xs text-slate-600">{getCaseTitle(tc)}</p>
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${state?.script ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                                                {state?.script ? 'Script ready' : 'No script'}
                                            </span>
                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${state?.screenName ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                                                {state?.screenName || 'No screen'}
                                            </span>
                                            {state?.run && (
                                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${state.run.passed ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                                                    {state.run.passed ? 'Run passed' : 'Run failed'}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* RIGHT: detail */}
                <section className="soft-card">
                    {!selectedCase || !selectedState ? (
                        <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-slate-500">
                            Select a test case to inspect.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-bold text-slate-900">{selectedCase.test_case_no}</h3>
                                        {selectedCase._category && (
                                            <span className={`rounded px-2 py-0.5 text-xs font-semibold ${
                                                (selectedCase._category || '').toLowerCase() === 'negative'
                                                    ? 'bg-red-50 text-red-700 border border-red-200'
                                                    : 'bg-green-50 text-green-700 border border-green-200'
                                            }`}>
                                                {(selectedCase._category || '').toLowerCase() === 'negative' ? 'Negative Test Case' : 'Positive Test Case'}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1 text-sm text-slate-600">{getCaseTitle(selectedCase)}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={generateForCase}
                                    disabled={isBusy}
                                    className="primary-btn shrink-0 bg-gradient-to-r from-emerald-600 to-green-600 px-4 disabled:opacity-50"
                                >
                                    {selectedState.status === 'saving' || selectedState.status === 'generating'
                                        ? <Loader2 className="h-4 w-4 animate-spin" />
                                        : <Wand2 className="h-4 w-4" />}
                                    {selectedState.script ? 'Regenerate Script' : 'Generate Script'}
                                </button>
                            </div>

                            {selectedState.error && (
                                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    <AlertCircle className="mt-0.5 h-4 w-4" /><span>{selectedState.error}</span>
                                </div>
                            )}

                            {/* Tabs */}
                            <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
                                {TABS.map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                                            activeTab === tab.id
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Script tab */}
                            {activeTab === 'script' && (
                                selectedState.script ? (
                                    <div className="space-y-2">
                                        <div className="flex flex-wrap items-center justify-end gap-2">
                                            <button type="button" onClick={() => copyScript(selectedState.script)} className="secondary-btn px-3 py-1.5 text-xs">
                                                <Copy className="h-3.5 w-3.5" /> Copy
                                            </button>
                                            <button type="button" onClick={() => downloadScript(selectedCase, selectedState.script)} className="secondary-btn px-3 py-1.5 text-xs">
                                                <Download className="h-3.5 w-3.5" /> Download
                                            </button>
                                        </div>
                                        <textarea
                                            value={selectedState.script}
                                            onChange={(e) => {
                                                patchCase(selectedKey, { script: e.target.value });
                                                markScriptDirty(selectedKey);
                                            }}
                                            rows={16}
                                            spellCheck={false}
                                            className="text-input font-mono text-xs"
                                        />
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button type="button" onClick={saveScript} disabled={isBusy} className="secondary-btn px-3 py-2 text-sm disabled:opacity-50">
                                                {selectedState.status === 'saving-script' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                                            </button>
                                            {(selectedState.inputSets || []).length > 0 && (
                                                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                                                    <span className="font-medium">Data set:</span>
                                                    <select
                                                        value={selectedState.runSetId || selectedState.inputSets[0]?.id || ''}
                                                        onChange={(e) => patchCase(selectedKey, { runSetId: e.target.value })}
                                                        disabled={isBusy}
                                                        title="Input set whose values are filled into the app for this run"
                                                        className="text-input px-2 py-1.5 text-xs disabled:opacity-50"
                                                    >
                                                        {(selectedState.inputSets || []).map((set) => (
                                                            <option key={set.id} value={set.id}>{set.name || 'Set'}</option>
                                                        ))}
                                                    </select>
                                                </label>
                                            )}
                                            <button type="button" onClick={runScript} disabled={isBusy} className="primary-btn bg-gradient-to-r from-emerald-600 to-green-600 px-3 py-2 text-sm disabled:opacity-50">
                                                {selectedState.status === 'running' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run on emulator
                                            </button>
                                        </div>
                                        {(selectedState.assumptions?.length > 0 || selectedState.warnings?.length > 0) && (
                                            <div className="grid gap-3 sm:grid-cols-2">
                                                {selectedState.assumptions?.length > 0 && (
                                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                                        <p className="text-xs font-semibold text-slate-900">Assumptions</p>
                                                        <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-slate-600">
                                                            {selectedState.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                                                        </ul>
                                                    </div>
                                                )}
                                                {selectedState.warnings?.length > 0 && (
                                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                                        <p className="text-xs font-semibold text-amber-800">Warnings</p>
                                                        <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-amber-700">
                                                            {selectedState.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 py-14 text-center text-slate-500">
                                        <FileCode className="h-8 w-8 text-slate-400" />
                                        <p className="mt-3 text-sm font-semibold text-slate-900">No script generated yet</p>
                                        <p className="mt-1 text-sm text-slate-600">Pick a screen/POM in the POMs tab, then generate the Appium script for this test case.</p>
                                    </div>
                                )
                            )}

                            {/* POMs tab — auto-filled locators */}
                            {activeTab === 'poms' && (
                                <div className="space-y-3">
                                    <div className="grid gap-3 md:grid-cols-4">
                                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            POM (page)
                                            <select
                                                value={selectedState.screenName}
                                                onChange={(e) => handleScreenChange(selectedKey, e.target.value)}
                                                className="text-input mt-1 normal-case"
                                            >
                                                {builtPoms.map((p) => (
                                                    <option key={p.pageName} value={p.pageName}>{p.pageName}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            POM Name
                                            <input value={selectedState.pomName} onChange={(e) => patchCase(selectedKey, { pomName: e.target.value })} className="text-input mt-1 normal-case" />
                                        </label>
                                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            App Package
                                            <input value={selectedState.appPackage} onChange={(e) => patchCase(selectedKey, { appPackage: e.target.value })} className="text-input mt-1 normal-case" />
                                        </label>
                                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            App Activity
                                            <input value={selectedState.appActivity} onChange={(e) => patchCase(selectedKey, { appActivity: e.target.value })} className="text-input mt-1 normal-case" />
                                        </label>
                                    </div>

                                    {!selectedState.confident && (
                                        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                            Low-confidence screen match — confirm the screen above.
                                        </p>
                                    )}

                                    <div className="flex items-center justify-between">
                                        <h5 className="text-sm font-semibold text-slate-900">
                                            Auto-filled locators <span className="text-slate-500">({selectedState.locators.length})</span>
                                        </h5>
                                        <div className="flex items-center gap-2">
                                            <button type="button" onClick={() => setInspectorOpen(true)} className="primary-btn px-3 py-1.5 text-xs">
                                                <Smartphone className="h-3.5 w-3.5" /> Pick from device
                                            </button>
                                            <button type="button" onClick={() => addLocator(selectedKey)} className="secondary-btn px-3 py-1.5 text-xs">
                                                <Plus className="h-3.5 w-3.5" /> Add
                                            </button>
                                        </div>
                                    </div>
                                    <div className="overflow-hidden rounded-lg border border-slate-200">
                                        <table className="w-full text-left text-xs">
                                            <thead className="bg-slate-100 text-slate-600">
                                                <tr>
                                                    <th className="px-2 py-1.5 font-semibold">Name</th>
                                                    <th className="px-2 py-1.5 font-semibold">Strategy</th>
                                                    <th className="px-2 py-1.5 font-semibold">Value</th>
                                                    <th className="px-2 py-1.5"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedState.locators.map((loc) => (
                                                    <tr key={loc._id} className="border-t border-slate-100">
                                                        <td className="px-2 py-1"><input value={loc.name} onChange={(e) => updateLocator(selectedKey, loc._id, 'name', e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1" /></td>
                                                        <td className="px-2 py-1">
                                                            <select value={loc.strategy} onChange={(e) => updateLocator(selectedKey, loc._id, 'strategy', e.target.value)} className="w-full rounded border border-slate-200 px-1 py-1">
                                                                {STRATEGY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                            </select>
                                                        </td>
                                                        <td className="px-2 py-1"><input value={loc.value} onChange={(e) => updateLocator(selectedKey, loc._id, 'value', e.target.value)} className="w-full rounded border border-slate-200 px-2 py-1 font-mono" /></td>
                                                        <td className="px-2 py-1 text-right">
                                                            <button type="button" onClick={() => removeLocator(selectedKey, loc._id)} className="inline-flex items-center rounded p-1 text-red-600 hover:bg-red-50" aria-label="Remove locator">
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Run Output tab */}
                            {activeTab === 'run' && (
                                selectedState.run ? (
                                    <div className={`rounded-lg border p-3 text-xs ${selectedState.run.passed ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                                        <p className="flex items-center gap-2 font-semibold">
                                            {selectedState.run.passed ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                            {selectedState.run.passed ? 'Run passed' : 'Run did not pass'}
                                            {typeof selectedState.run.exit_code !== 'undefined' ? ` (exit ${selectedState.run.exit_code})` : ''}
                                            {selectedState.run.set_name ? ` • data set: ${selectedState.run.set_name}` : ''}
                                            {typeof selectedState.run.screenshots === 'number' ? ` • ${selectedState.run.screenshots} screenshot${selectedState.run.screenshots === 1 ? '' : 's'}` : ''}
                                        </p>
                                        {Array.isArray(selectedState.run.runtime_notes) && selectedState.run.runtime_notes.length > 0 && (
                                            <p className="mt-1 text-[11px] text-slate-600">{selectedState.run.runtime_notes.join(' • ')}</p>
                                        )}
                                        <button
                                            type="button"
                                            onClick={downloadReport}
                                            disabled={selectedState.reportLoading}
                                            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                                            title={(selectedState.run.target || runTarget) === 'browserstack'
                                                ? 'Download a PDF report — includes the BrowserStack session details, logs and video link'
                                                : 'Download a PDF report of this run'}
                                        >
                                            {selectedState.reportLoading
                                                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing report…</>
                                                : <><Download className="h-3.5 w-3.5" /> Download report (PDF)</>}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={downloadExcelReport}
                                            disabled={excelReport.loading}
                                            className="mt-2 ml-2 inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                                            title="Download an Excel execution report of all test cases (with Date, Time, Author, BRD name and Session on top), ready for the client to fill in results"
                                        >
                                            {excelReport.loading
                                                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing Excel…</>
                                                : <><Download className="h-3.5 w-3.5" /> Download report (Excel)</>}
                                        </button>
                                        {excelReport.error && (
                                            <p className="mt-1 text-[11px] font-medium text-rose-600">{excelReport.error}</p>
                                        )}
                                        {selectedState.run.browserstack_session_url && (
                                            <a
                                                href={selectedState.run.browserstack_session_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-[11px] font-semibold text-orange-700 hover:bg-orange-100"
                                            >
                                                <Cloud className="h-3.5 w-3.5" /> Watch this run on BrowserStack
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </a>
                                        )}
                                        {Array.isArray(selectedState.run.unresolved_inputs) && selectedState.run.unresolved_inputs.length > 0 && (
                                            <p className="mt-1 rounded bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800">
                                                No value for: {selectedState.run.unresolved_inputs.join(', ')} — left as placeholder. Add it to the selected data set.
                                            </p>
                                        )}
                                        {(selectedState.run.stdout || selectedState.run.stderr) && (
                                            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-slate-700">
                                                {`${selectedState.run.stdout || ''}\n${selectedState.run.stderr || ''}`.trim()}
                                            </pre>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-12 text-slate-500">
                                        <Play className="h-8 w-8" />
                                        <p className="text-sm">No run yet. Generate a script, then Run on emulator.</p>
                                    </div>
                                )
                            )}

                            {/* Metadata tab — User Role, Tab Flows, User Input Sets (the test data) */}
                            {activeTab === 'metadata' && (
                                <div className="space-y-4">
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="rounded-lg border border-slate-200 p-3">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">User Role</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-900">{selectedCase.User_Role || selectedCase.role || selectedCase.actor || '—'}</p>
                                        </div>
                                        <div className="rounded-lg border border-slate-200 p-3">
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tab Flows</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-900">{tabFlowsText(selectedCase) || '—'}</p>
                                        </div>
                                    </div>

                                    {/* User Input Sets editor — the data the script's {USER_INPUT:field} placeholders use */}
                                    <div className="rounded-lg border border-slate-200 p-3">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-semibold text-slate-900">
                                                User Input Sets <span className="text-slate-500">(up to 3) • {(selectedState.inputSets || []).length}</span>
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => addInputSet(selectedKey)}
                                                disabled={(selectedState.inputSets || []).length >= 3}
                                                className="secondary-btn px-3 py-1.5 text-xs disabled:opacity-50"
                                            >
                                                <Plus className="h-3.5 w-3.5" /> Add Set
                                            </button>
                                        </div>
                                        <p className="mt-1 text-xs text-slate-500">These are the exact key-values filled into the app — generated for this test case when you Generate the script. The run fills the app ONLY from these; edit any value here to change what's typed.</p>

                                        {(selectedState.inputSets || []).length === 0 && (
                                            <p className="mt-2 rounded border border-dashed border-slate-300 px-3 py-3 text-center text-xs text-slate-500">
                                                No input sets on this test case.
                                            </p>
                                        )}

                                        <div className="mt-2 space-y-3">
                                            {(selectedState.inputSets || []).map((set, si) => (
                                                <div key={set.id} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                                                    <div className="flex items-center justify-between">
                                                        <span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white">{set.name || `Set ${si + 1}`}</span>
                                                        <div className="flex gap-2">
                                                            <button type="button" onClick={() => addInputRow(selectedKey, set.id)} className="text-xs font-semibold text-emerald-700 hover:underline">+ Add row</button>
                                                            <button type="button" onClick={() => removeInputSet(selectedKey, set.id)} className="text-xs font-semibold text-red-600 hover:underline">Remove set</button>
                                                        </div>
                                                    </div>
                                                    <table className="mt-2 w-full text-left text-xs">
                                                        <thead className="text-slate-500">
                                                            <tr><th className="py-1 font-semibold">Field</th><th className="py-1 font-semibold">Value</th><th></th></tr>
                                                        </thead>
                                                        <tbody>
                                                            {set.rows.map((row) => (
                                                                <tr key={row.id}>
                                                                    <td className="py-1 pr-2"><input value={row.field} onChange={(e) => updateInputRow(selectedKey, set.id, row.id, 'field', e.target.value)} placeholder="email" className="w-full rounded border border-slate-200 px-2 py-1" /></td>
                                                                    <td className="py-1 pr-2"><input value={row.value} onChange={(e) => updateInputRow(selectedKey, set.id, row.id, 'value', e.target.value)} placeholder="testuser@example.com" className="w-full rounded border border-slate-200 px-2 py-1" /></td>
                                                                    <td className="py-1 text-right"><button type="button" onClick={() => removeInputRow(selectedKey, set.id, row.id)} className="inline-flex items-center rounded p-1 text-red-600 hover:bg-red-100" aria-label="Remove row"><Trash2 className="h-3.5 w-3.5" /></button></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">Test Steps</p>
                                        <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-slate-700">
                                            {asLines(selectedCase.test_steps || selectedCase.steps).map((s, i) => <li key={i}>{s}</li>)}
                                            {asLines(selectedCase.test_steps || selectedCase.steps).length === 0 && <li className="list-none text-slate-400">No steps provided.</li>}
                                        </ol>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">Expected Result</p>
                                        <p className="mt-1 text-sm text-slate-700">
                                            {selectedCase.expected_result || selectedCase.expected || '—'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Continue to Automation — runs every generated script on the chosen target */}
                            <div className="flex flex-col gap-3 border-t border-slate-200 pt-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Run on</span>
                                    {renderRunTargetToggle()}
                                </div>
                                <div className="flex flex-col items-stretch gap-2 sm:items-end">
                                <button
                                    type="button"
                                    onClick={continueToAutomation}
                                    disabled={automationRunning || isBusy}
                                    className="primary-btn h-11 px-6 bg-gradient-to-r from-emerald-600 to-green-600 disabled:opacity-60"
                                >
                                    {automationRunning
                                        ? <Loader2 className="h-4 w-4 animate-spin" />
                                        : <Play className="h-4 w-4" />}
                                    {automationRunning
                                        ? 'Running automation on emulator…'
                                        : runnableCount > 0
                                            ? `Continue to Automation — run ${runnableCount} script${runnableCount === 1 ? '' : 's'}`
                                            : 'Continue to Automation'}
                                    {!automationRunning && <ArrowRight className="h-4 w-4" />}
                                </button>
                                <p className="text-xs text-slate-500">
                                    {runnableCount > 0
                                        ? (runTarget === 'browserstack'
                                            ? 'Runs each generated script on a BrowserStack cloud device — the app is filled strictly from each test case\'s metadata (User Input Sets). Watch the Run Output tab.'
                                            : 'Boots the local Android emulator (Android Studio) and runs each generated script — the app is filled strictly from each test case\'s metadata (User Input Sets). Watch the Run Output tab.')
                                        : `Generate a script first, then this runs the automation on ${runTarget === 'browserstack' ? 'a BrowserStack cloud device' : 'the local emulator (Android Studio)'}.`}
                                </p>
                              </div>
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {inspectorOpen && selectedState && (
                <MobileInspectorModal
                    pdfId={pdfId}
                    appPackage={selectedState.appPackage}
                    appActivity={selectedState.appActivity}
                    onPick={(picked) => addLocatorFromInspector(selectedKey, picked)}
                    onClose={() => setInspectorOpen(false)}
                />
            )}
        </div>
    );
};

export default MobileAutoTestStep;
